// SCR-PUR-01/02 Purchasing and SCR-PUR-05 order card (frozen mockups-pur-v1, ADR-087). Owner and Storekeeper; the
// Storekeeper sees quantities only and has no order actions except "Receive" (GEN-15, ADR-097).
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  EmptyState,
  List,
  ListRow,
  Notice,
  Progress,
  QuickActions,
  SkeletonList,
  SummaryCard,
  Tabs,
  type QuickAction,
  VirtualList,
} from '../../design/kit';
import {
  PURCHASING,
  loadOrders,
  loadReceipts,
  loadReturns,
  loadSummary,
  type PoStatus,
  type PurchaseOrder,
  type Receipt,
  type SupplierReturn,
} from '../../data/purchasing';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { initials, useTab } from '../catalog/common';
import { CancelSheet, CloseSheet, PurchaseSheet, ReceiveSheet, ReturnSheet, unitQty } from './PurchaseSheets';

const TABS = ['orders', 'receipts', 'returns'] as const;
type Tab = (typeof TABS)[number];
type Sheet = 'purchase' | 'return' | null;
const OPEN: PoStatus[] = ['sent', 'partial'];

function useFmt() {
  const { i18n } = useTranslation();
  const lang = i18n.language as Language;
  return { lang, fmt: (v: string | number) => formatMoney(v, lang), day: (d: string) => formatDate(d, lang) };
}

function StatusBadges({ order }: { order: PurchaseOrder }) {
  const { t } = useTranslation();
  return (
    <>
      <Badge tone={OPEN.includes(order.status) ? 'info' : order.status === 'received' ? 'success' : 'neutral'}>
        {t(`st.${order.status}`)}
      </Badge>
      {order.late && <Badge tone="warning">{t('b.late')}</Badge>}
    </>
  );
}

export function OrderRows({ orders, onReceive }: { orders: PurchaseOrder[]; onReceive?: (o: PurchaseOrder) => void }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const { fmt, day } = useFmt();
  const navigate = useNavigate();
  return (
    <VirtualList
      label={t('tab.orders')}
      items={orders}
      rowKey={(o) => o.id}
      row={(o) => (
        <ListRow
          testId={`po-${o.number}`}
          lead={{ initials: initials(o.supplier) }}
          title={<Name>{o.supplier}</Name>}
          onOpen={() => navigate(`/purchasing/${o.id}`)}
          sub={
            <>
              <Code>{o.number}</Code>
              <br />
              {t('pu.ordered', { d: day(o.order_date) })}
              {o.expected_date && (
                <>
                  <br />
                  {t('pu.expected', { d: day(o.expected_date) })}
                </>
              )}
              <Progress
                done={o.received_pieces}
                total={o.ordered_pieces}
                name={t('rc.done')}
                label={t('pu.progress', { a: o.received_pieces, b: o.ordered_pieces })}
              />
            </>
          }
          value={role === 'owner' && o.total !== null ? fmt(o.total) : undefined}
          badge={<StatusBadges order={o} />}
          actions={
            onReceive && OPEN.includes(o.status) ? (
              <Button pill onClick={() => onReceive(o)}>
                {t('act.receive')}
              </Button>
            ) : undefined
          }
        />
      )}
    />
  );
}

export function ReceiptRows({ receipts }: { receipts: Receipt[] }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const { fmt, day } = useFmt();
  return (
    <VirtualList
      label={t('tab.receipts')}
      items={receipts}
      rowKey={(r) => r.id}
      row={(r) => (
        <ListRow
          testId={`rec-${r.number}`}
          lead={{ icon: 'cart' }}
          title={<Name>{r.supplier}</Name>}
          sub={
            <>
              <Code>{r.number}</Code> · {day(r.business_date)}
              {r.po_number && (
                <>
                  {' · '}
                  <Code>{r.po_number}</Code>
                </>
              )}
              {r.lines.map((l) => (
                <span key={l.id}>
                  <br />
                  <Name>{l.product}</Name> {unitQty(t, l.unit, l.quantity)}
                </span>
              ))}
            </>
          }
          value={
            role === 'owner' && r.total !== null
              ? `${fmt(r.total)} · ${t('pu.paidfrom', { w: t(`cash.${r.paid_from ?? 'garage'}`) })}`
              : undefined
          }
          badge={r.delivery === 'pickup' ? <Badge tone="primary">{t('b.pickup')}</Badge> : undefined}
        />
      )}
    />
  );
}

export function ReturnRows({ returns }: { returns: SupplierReturn[] }) {
  const { t } = useTranslation();
  const { role } = useSession();
  const { fmt, day } = useFmt();
  return (
    <VirtualList
      label={t('tab.returns')}
      items={returns}
      rowKey={(x) => x.id}
      row={(x) => (
        <ListRow
          testId={`rts-${x.number}`}
          lead={{ icon: 'return' }}
          title={<Name>{x.supplier}</Name>}
          sub={
            <>
              <Code>{x.number}</Code> · {day(x.business_date)} · {t(`loc.${x.source}`)} ·{' '}
              <Code>{x.receipt_number}</Code>
              <br />
              <Name>{x.product}</Name> {unitQty(t, x.unit, x.quantity)} · <span dir="auto">{x.reason}</span>
            </>
          }
          value={
            role === 'owner' && x.refund !== null
              ? `${fmt(x.refund)} · ${t('pu.paidfrom', { w: t(`cash.${x.refund_to ?? 'garage'}`) })}`
              : undefined
          }
        />
      )}
    />
  );
}

export function PurchasingPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const owner = role === 'owner';
  const { fmt } = useFmt();
  const [tab, setTab] = useTab(TABS, 'orders');
  const [sheet, setSheet] = useState<Sheet>(null);
  const [receiving, setReceiving] = useState<PurchaseOrder | null>(null);
  const summary = useQuery({ queryKey: [...PURCHASING, 'summary'], queryFn: loadSummary });
  const orders = useQuery({
    queryKey: [...PURCHASING, 'orders'],
    queryFn: () => loadOrders(),
    enabled: tab === 'orders',
  });
  const receipts = useQuery({
    queryKey: [...PURCHASING, 'receipts'],
    queryFn: () => loadReceipts(),
    enabled: tab === 'receipts',
  });
  const returns = useQuery({
    queryKey: [...PURCHASING, 'returns'],
    queryFn: () => loadReturns(),
    enabled: tab === 'returns',
  });
  const close = () => setSheet(null);

  if (summary.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  const s = summary.data;
  const actions: QuickAction[] = owner
    ? [
        { id: 'purchase', label: t('act.purchase'), icon: 'plus', main: true, onSelect: () => setSheet('purchase') },
        { id: 'return', label: t('act.supreturn'), icon: 'return', onSelect: () => setSheet('return') },
      ]
    : [{ id: 'return', label: t('act.supreturn'), icon: 'return', main: true, onSelect: () => setSheet('return') }];
  const purchaseAction = owner ? (
    <Button variant="main" icon="plus" onClick={() => setSheet('purchase')}>
      {t('act.purchase')}
    </Button>
  ) : undefined;
  const list = tab === 'orders' ? orders : tab === 'receipts' ? receipts : returns;

  return (
    <>
      {s ? (
        <SummaryCard
          label={t('pu.awaiting')}
          value={String(s.awaiting)}
          figures={[
            { label: t('pu.late'), value: String(s.late) },
            owner
              ? { label: t('pu.month'), value: fmt(s.received_month ?? 0) }
              : { label: t('pu.monthn'), value: String(s.receipts_month) },
          ]}
        />
      ) : (
        <SkeletonList rows={1} label={t('loading')} />
      )}
      <QuickActions actions={actions} />
      <Tabs
        label={t('p.purchasing')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {!list.data ? (
        <SkeletonList label={t('loading')} />
      ) : list.data.length === 0 ? (
        tab === 'returns' ? (
          <EmptyState icon="return" title={t('empty.returns')} text={t('empty.returns.text')} />
        ) : (
          <EmptyState icon="cart" title={t('empty.orders')} text={t('empty.orders.text')} action={purchaseAction} />
        )
      ) : tab === 'orders' ? (
        <OrderRows orders={orders.data ?? []} onReceive={setReceiving} />
      ) : tab === 'receipts' ? (
        <ReceiptRows receipts={receipts.data ?? []} />
      ) : (
        <ReturnRows returns={returns.data ?? []} />
      )}
      {sheet === 'purchase' && <PurchaseSheet onClose={close} />}
      {sheet === 'return' && <ReturnSheet onClose={close} />}
      {receiving && <ReceiveSheet order={receiving} onClose={() => setReceiving(null)} />}
    </>
  );
}

type CardTab = 'lines' | 'receipts';
const CARD_TABS: CardTab[] = ['lines', 'receipts'];

/** SCR-PUR-05: one purchase order — lines and the receipts against it; owner: close remainder or cancel. */
export function OrderCard() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { role } = useSession();
  const owner = role === 'owner';
  const { fmt, day } = useFmt();
  const navigate = useNavigate();
  const [tab, setTab] = useTab(CARD_TABS, 'lines');
  const [sheet, setSheet] = useState<'receive' | 'close' | 'cancel' | null>(null);
  const orders = useQuery({ queryKey: [...PURCHASING, 'orders'], queryFn: () => loadOrders() });
  const order = orders.data?.find((o) => o.id === id);
  const receipts = useQuery({
    queryKey: [...PURCHASING, 'receipts', order?.supplier_id],
    queryFn: () => loadReceipts(order?.supplier_id),
    enabled: Boolean(order) && tab === 'receipts',
  });
  const back = useCallback(() => navigate('/purchasing'), [navigate]);
  useCardHeader(order ? order.number : t('p.purchasing'), back);

  if (orders.isLoading) return <SkeletonList label={t('loading')} />;
  if (!order) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  const open = OPEN.includes(order.status);
  const actions: QuickAction[] = [
    ...(open
      ? [
          {
            id: 'receive',
            label: t('act.receive'),
            icon: 'box' as const,
            main: true,
            onSelect: () => setSheet('receive'),
          },
        ]
      : []),
    ...(owner && order.status === 'partial'
      ? [{ id: 'close', label: t('act.closerest'), icon: 'check' as const, onSelect: () => setSheet('close') }]
      : []),
    ...(owner && order.status === 'sent'
      ? [{ id: 'cancel', label: t('act.cancelpo'), icon: 'close' as const, onSelect: () => setSheet('cancel') }]
      : []),
  ];
  const mine = (receipts.data ?? []).filter((r) => r.po_id === order.id);
  return (
    <>
      <SummaryCard
        label={order.supplier}
        value={t(`st.${order.status}`)}
        sub={
          <>
            {t('pu.ordered', { d: day(order.order_date) })}
            {order.expected_date && ` · ${t('pu.expected', { d: day(order.expected_date) })}`}
            {order.late && ` · ${t('b.late')}`}
          </>
        }
        figures={
          owner && order.total !== null
            ? [
                { label: t('pu.total'), value: fmt(order.total) },
                { label: t('rc.done'), value: fmt(order.received_value ?? 0) },
              ]
            : []
        }
      />
      <Progress
        done={order.received_pieces}
        total={order.ordered_pieces}
        name={t('rc.done')}
        label={t('pu.progress', { a: order.received_pieces, b: order.ordered_pieces })}
      />
      {order.close_reason && <Notice tone="info">{`${t('st.closed')} · ${order.close_reason}`}</Notice>}
      <QuickActions actions={actions} />
      <Tabs
        label={order.number}
        value={tab}
        onChange={(v) => setTab(v as CardTab)}
        items={CARD_TABS.map((v) => ({ id: v, label: t(`tab.${v}`) }))}
      />
      {tab === 'lines' ? (
        <List label={t('tab.lines')}>
          {order.lines.map((l) => (
            <ListRow
              key={l.id}
              testId={`line-${l.sku ?? l.id}`}
              lead={{ icon: 'tag' }}
              title={<Name>{l.product}</Name>}
              sub={
                <>
                  {t('rc.ordered')} {unitQty(t, l.unit, l.quantity)} · {t('rc.done')} {unitQty(t, l.unit, l.received)} ·{' '}
                  {t('rc.left')} {unitQty(t, l.unit, l.quantity - l.received)}
                  {owner && l.unit_price !== null && (
                    <>
                      <br />
                      {fmt(l.unit_price)} {t(l.unit === 'box' ? 'perbox' : 'perpiece')}
                    </>
                  )}
                </>
              }
              value={owner && l.unit_price !== null ? fmt(l.quantity * l.unit_price) : undefined}
              badge={l.received >= l.quantity ? <Badge tone="success">{t('rc.complete')}</Badge> : undefined}
            />
          ))}
        </List>
      ) : !receipts.data ? (
        <SkeletonList label={t('loading')} />
      ) : mine.length === 0 ? (
        <EmptyState icon="cart" title={t('po.receipts')} text={t('empty.orders.text')} />
      ) : (
        <ReceiptRows receipts={mine} />
      )}
      {sheet === 'receive' && <ReceiveSheet order={order} onClose={() => setSheet(null)} />}
      {sheet === 'close' && <CloseSheet order={order} onClose={() => setSheet(null)} />}
      {sheet === 'cancel' && <CancelSheet order={order} onClose={() => setSheet(null)} />}
    </>
  );
}
